import React, { useState, useEffect } from "react";
import axios from "axios";
import { DoorOpen, Camera, Fingerprint, ScanFace, CreditCard, Server, Cpu, ShieldCheck, Loader2, ArrowLeft, CalendarClock, Download, KeyRound, Video, BrainCircuit, Check, AlertTriangle, Network, Wrench, Box, FileText, Package, LogOut } from "lucide-react";
import { SecurestSundries } from "@/components/SecurestSundries";
import { SecurestSavedQuotes, downloadPdf } from "@/components/SecurestSavedQuotes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/securest`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const ACCENT = "#4fb3a4";      // Securest muted teal
const ACCENT_DK = "#2f8375";
const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const READER_ICONS = { face: ScanFace, finger: Fingerprint, rfid: CreditCard };
const CATEGORY_ICONS = {
  Readers: ScanFace, Controllers: Cpu, Enclosures: Server,
  "Software Licence": ShieldCheck, Cameras: Camera, NVR: Server, Enrolment: Fingerprint, Analytics: BrainCircuit,
  "Wall Mounts": Wrench, Backboxes: Box, Network: Network, Customisation: Wrench, Sundries: Package,
};

const EXTRAS = [
  { key: "access_control_licences", label: "Add Access Control licences", hint: "Flags that more licences are needed", icon: KeyRound },
  { key: "cctv_licences", label: "Add CCTV licences", hint: "Flags that more licences are needed", icon: Video },
  { key: "edge_analytics", label: "Add Edge X1 analytics box", hint: "NVR-IVA-EDGE-X1-16CH", icon: BrainCircuit },
];

export default function Securest({ onBack, onLogout }) {
  const [doors, setDoors] = useState(2);
  const [readerType, setReaderType] = useState("face");
  const [cameras, setCameras] = useState(4);
  const [installDate, setInstallDate] = useState("");
  const [dateError, setDateError] = useState(false);
  const [customerRef, setCustomerRef] = useState("");
  const [saving, setSaving] = useState(false);
  const [readers, setReaders] = useState([]);
  const [bom, setBom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extras, setExtras] = useState({ access_control_licences: false, cctv_licences: false, edge_analytics: false });
  const [lastReq, setLastReq] = useState(null);
  const [wallMounts, setWallMounts] = useState(null);
  const [wallDialog, setWallDialog] = useState(false);
  const [poeOn, setPoeOn] = useState(false);
  const [poePorts, setPoePorts] = useState(0);
  const [sundries, setSundries] = useState([]);
  const [showSaved, setShowSaved] = useState(false);

  const pickReader = (id) => {
    setReaderType(id);
    if (id === "face") setWallDialog(true);
  };
  const answerWallMounts = (yes) => {
    setWallMounts(yes);
    setWallDialog(false);
    if (installDate) build(yes);
  };

  useEffect(() => {
    axios.get(`${API}/options`, { headers: authHeaders() })
      .then((r) => setReaders(r.data.readers || []))
      .catch(() => setReaders([
        { id: "face", label: "Face" }, { id: "finger", label: "Fingerprint" }, { id: "rfid", label: "RFID / Keypad" },
      ]));
  }, []);

  const build = async (wallAnswer) => {
    const wm = typeof wallAnswer === "boolean" ? wallAnswer : wallMounts;
    if (!installDate) {
      setDateError(true);
      alert("Please enter the expected install date before building the system.");
      return;
    }
    if (readerType === "face" && Number(doors) > 0 && wm === null) {
      setWallDialog(true);
      return;
    }
    if (poeOn && !poePorts) {
      alert("Please choose a PoE switch size (4, 8, 16 or 24 ports).");
      return;
    }
    setDateError(false);
    setLoading(true);
    try {
      const req = {
        doors: Number(doors) || 0, reader_type: readerType, cameras: Number(cameras) || 0, install_date: installDate, ...extras,
        wall_mounts: readerType === "face" && !!wm, poe_switch: poeOn ? poePorts : 0,
        sundries: sundries.filter((s) => s.description.trim() && Number(s.qty) > 0)
          .map((s) => ({ description: s.description.trim(), qty: Number(s.qty), unit_price: Number(s.unit_price) || 0 })),
      };
      const r = await axios.post(`${API}/build-bom`, req, { headers: authHeaders() });
      setBom(r.data);
      setLastReq(req);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not build the system. Please try again.");
    }
    setLoading(false);
  };

  const fmtDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return d; }
  };
  const today = new Date().toISOString().split("T")[0];

  const downloadAndEmail = async () => {
    if (!lastReq) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/download-and-email`, { ...lastReq, customer_reference: customerRef }, { headers: authHeaders() });
      downloadPdf(r.data.pdf_base64, r.data.filename);
      const emailed = r.data.email_status === "sent";
      alert(`Quote ${r.data.quote_ref} downloaded.\n${emailed ? `A copy has been emailed to ${r.data.emailed_to.join(", ")}.` : `The quote downloaded, but the email could not be sent.\n\nReason: ${r.data.email_error || "unknown error"}`}`);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not create the quote. Please try again.");
    }
    setSaving(false);
  };

  return (
    <div style={{ backgroundColor: "#0a0e12", minHeight: "100vh", color: "#e2e8f0", fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Header */}
      <header className="border-b" style={{ borderColor: "#1c2530", backgroundColor: "#0c1218" }}>
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md flex items-center justify-center" style={{ backgroundColor: ACCENT }}>
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <div>
              <div style={{ fontFamily: "Georgia, 'Times New Roman', serif", letterSpacing: "0.02em" }} className="text-xl font-semibold text-white leading-tight">Securest</div>
              <div className="text-xs" style={{ color: ACCENT }}>Mortuary Security Configurator</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="securest-saved-open" onClick={() => setShowSaved(true)} className="text-xs px-3 py-1.5 rounded border flex items-center gap-1 transition-colors hover:bg-white/5" style={{ borderColor: ACCENT, color: ACCENT }}>
              <FileText className="h-3.5 w-3.5" /> Saved quotes
            </button>
          {onLogout && (
            <button data-testid="securest-logout" onClick={onLogout} className="text-xs px-3 py-1.5 rounded border flex items-center gap-1" style={{ borderColor: "#28323e", color: "#94a3b8" }}>
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          )}
          {onBack && (
            <button data-testid="securest-back" onClick={onBack} className="text-xs px-3 py-1.5 rounded border flex items-center gap-1" style={{ borderColor: "#28323e", color: "#94a3b8" }}>
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
          )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 style={{ fontFamily: "Georgia, serif" }} className="text-3xl sm:text-4xl font-semibold text-white">Build a mortuary security system</h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "#8b98a8" }}>
            Tell us how many doors and cameras you need — we’ll build the complete Bio-Metric Access Control &amp; CCTV bill of materials automatically. Every door is fitted read-in and read-out.
          </p>
        </div>

        {/* Configurator card */}
        <div className="rounded-xl border p-6 mb-8" style={{ borderColor: "#1c2530", backgroundColor: "#0f151c" }}>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Doors */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-white mb-3"><DoorOpen className="h-4 w-4" style={{ color: ACCENT }} /> How many doors?</label>
              <input data-testid="securest-doors" type="number" min="0" value={doors} onChange={(e) => setDoors(e.target.value)}
                className="w-full rounded-lg px-4 py-3 text-lg text-white outline-none" style={{ backgroundColor: "#0a0e12", border: "1px solid #28323e" }} />
              <p className="text-xs mt-2" style={{ color: "#64748b" }}>Each door gets 2 readers (entry + exit).</p>

              <div className="mt-5">
                <div className="text-sm font-semibold text-white mb-3">Reader type</div>
                <div className="grid grid-cols-3 gap-2">
                  {readers.map((r) => {
                    const Icon = READER_ICONS[r.id] || CreditCard;
                    const active = readerType === r.id;
                    return (
                      <button key={r.id} data-testid={`securest-reader-${r.id}`} onClick={() => pickReader(r.id)}
                        className="rounded-lg px-2 py-3 flex flex-col items-center gap-2 transition-all"
                        style={{ backgroundColor: active ? "rgba(79,179,164,0.12)" : "#0a0e12", border: `1px solid ${active ? ACCENT : "#28323e"}` }}>
                        <Icon className="h-5 w-5" style={{ color: active ? ACCENT : "#94a3b8" }} />
                        <span className="text-xs font-medium" style={{ color: active ? "#fff" : "#94a3b8" }}>{r.label}</span>
                      </button>
                    );
                  })}
                </div>
                {readerType === "face" && wallMounts !== null && (
                  <button type="button" data-testid="securest-wallmount-status" onClick={() => setWallDialog(true)}
                    className="mt-2 text-xs underline-offset-2 hover:underline" style={{ color: ACCENT }}>
                    Wall mounts: {wallMounts ? "Yes" : "No"} — change
                  </button>
                )}
              </div>
            </div>

            {/* Cameras */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-white mb-3"><Camera className="h-4 w-4" style={{ color: ACCENT }} /> How many cameras?</label>
              <input data-testid="securest-cameras" type="number" min="0" value={cameras} onChange={(e) => setCameras(e.target.value)}
                className="w-full rounded-lg px-4 py-3 text-lg text-white outline-none" style={{ backgroundColor: "#0a0e12", border: "1px solid #28323e" }} />
              <p className="text-xs mt-2" style={{ color: "#64748b" }}>ZKBioSense 5MP turret cameras. ≤4 cameras add a 4-channel NVR; 5–8 add an 8-channel NVR.</p>
            </div>
          </div>

          {/* Expected install date — required */}
          <div className="mt-6 pt-6 border-t grid md:grid-cols-2 gap-6" style={{ borderColor: "#1c2530" }}>
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-white mb-3">
                <CalendarClock className="h-4 w-4" style={{ color: ACCENT }} /> Expected install date <span className="text-red-400">*</span>
              </label>
              <input data-testid="securest-install-date" type="date" min={today} value={installDate}
                onChange={(e) => { setInstallDate(e.target.value); setDateError(false); }}
                className="w-full rounded-lg px-4 py-3 text-white outline-none"
                style={{ backgroundColor: "#0a0e12", border: `1px solid ${dateError ? "#f87171" : "#28323e"}`, colorScheme: "dark" }} />
              {dateError && <p data-testid="securest-date-error" className="text-xs mt-2 text-red-400">Please enter the expected install date — this is required.</p>}
            </div>
            <div>
              <label className="text-sm font-semibold text-white mb-3 block">Customer / site reference</label>
              <input data-testid="securest-customer-ref" type="text" placeholder="e.g. Smith &amp; Sons Funeral Home, Preston" value={customerRef}
                onChange={(e) => setCustomerRef(e.target.value)}
                className="w-full rounded-lg px-4 py-3 text-white outline-none"
                style={{ backgroundColor: "#0a0e12", border: "1px solid #28323e" }} />
              <p className="text-xs mt-2" style={{ color: "#64748b" }}>Appears on the downloaded and emailed quote (optional).</p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t" style={{ borderColor: "#1c2530" }}>
            <div className="text-sm font-semibold text-white mb-3">Optional extras</div>
            <div className="grid sm:grid-cols-3 gap-2">
              {EXTRAS.map(({ key, label, hint, icon: Icon }) => {
                const on = extras[key];
                return (
                  <button key={key} type="button" data-testid={`securest-extra-${key}`} aria-pressed={on}
                    onClick={() => setExtras((x) => ({ ...x, [key]: !x[key] }))}
                    className="rounded-lg px-3 py-3 flex items-start gap-3 text-left transition-colors"
                    style={{ backgroundColor: on ? "rgba(79,179,164,0.12)" : "#0a0e12", border: `1px solid ${on ? ACCENT : "#28323e"}` }}>
                    <span className="h-5 w-5 mt-0.5 rounded flex items-center justify-center flex-shrink-0" style={{ border: `1px solid ${on ? ACCENT : "#3b4654"}`, backgroundColor: on ? ACCENT : "transparent" }}>
                      {on && <Check className="h-3.5 w-3.5 text-white" />}
                    </span>
                    <span>
                      <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: on ? "#fff" : "#cbd5e1" }}><Icon className="h-4 w-4" style={{ color: ACCENT }} />{label}</span>
                      <span className="block text-xs mt-1" style={{ color: "#64748b" }}>{hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-2">
              <button type="button" data-testid="securest-extra-poe" aria-pressed={poeOn}
                onClick={() => { setPoeOn((v) => !v); if (poeOn) setPoePorts(0); }}
                className="w-full rounded-lg px-3 py-3 flex items-center gap-3 text-left transition-colors"
                style={{ backgroundColor: poeOn ? "rgba(79,179,164,0.12)" : "#0a0e12", border: `1px solid ${poeOn ? ACCENT : "#28323e"}` }}>
                <span className="h-5 w-5 rounded flex items-center justify-center flex-shrink-0" style={{ border: `1px solid ${poeOn ? ACCENT : "#3b4654"}`, backgroundColor: poeOn ? ACCENT : "transparent" }}>
                  {poeOn && <Check className="h-3.5 w-3.5 text-white" />}
                </span>
                <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: poeOn ? "#fff" : "#cbd5e1" }}><Network className="h-4 w-4" style={{ color: ACCENT }} />Add PoE switch</span>
              </button>
              {poeOn && (
                <div className="grid grid-cols-4 gap-2 mt-2" data-testid="securest-poe-options">
                  {[4, 8, 16, 24].map((p) => (
                    <button key={p} type="button" data-testid={`securest-poe-${p}`} onClick={() => setPoePorts(p)}
                      className="rounded-lg py-2.5 text-sm font-medium transition-colors"
                      style={{ backgroundColor: poePorts === p ? ACCENT : "#0a0e12", color: poePorts === p ? "#fff" : "#94a3b8", border: `1px solid ${poePorts === p ? ACCENT : "#28323e"}` }}>
                      {p} ports
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <SecurestSundries rows={sundries} setRows={setSundries} />

          <button data-testid="securest-build" onClick={build} disabled={loading}
            className="mt-6 px-6 py-3 rounded-lg font-semibold text-white flex items-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: ACCENT }}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {loading ? "Building…" : "Build my system"}
          </button>
        </div>

        {/* BOM result */}
        {bom && (
          <div data-testid="securest-bom" className="rounded-xl border overflow-hidden" style={{ borderColor: "#1c2530", backgroundColor: "#0f151c" }}>
            <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "#1c2530" }}>
              <div>
                <div className="text-sm font-semibold text-white">Bill of Materials</div>
                <div className="text-xs" style={{ color: "#8b98a8" }}>{bom.doors} door(s) · {bom.cameras} camera(s){bom.install_date ? ` · Install: ${fmtDate(bom.install_date)}` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="securest-download-email" onClick={downloadAndEmail} disabled={saving || bom.lines.length === 0}
                  className="text-xs px-3 py-1.5 rounded font-semibold text-white flex items-center gap-1 disabled:opacity-60" style={{ backgroundColor: ACCENT }}>
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} {saving ? "Preparing…" : "Download & Email"}
                </button>
              </div>
            </div>

            {bom.lines.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm" style={{ color: "#64748b" }}>Enter at least one door or camera, then build your system.</div>
            ) : (
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: "#0c1218", color: "#8b98a8" }} className="text-left text-xs uppercase tracking-wide">
                    <th className="px-4 py-3">Item</th>
                    <th className="px-4 py-3">SKU</th>
                    <th className="px-4 py-3 text-center">Qty</th>
                    <th className="px-4 py-3 text-right">Unit price</th>
                  </tr>
                </thead>
                <tbody>
                  {bom.lines.map((l, i) => {
                    const Icon = CATEGORY_ICONS[l.category] || ShieldCheck;
                    return (
                      <tr key={i} className="border-t" style={{ borderColor: "#161d26" }}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 flex-shrink-0" style={{ color: ACCENT }} />
                            <div>
                              <div className="text-white">{l.description}</div>
                              <div className="text-xs" style={{ color: "#64748b" }}>{l.category}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ color: "#94a3b8" }}>{l.sku}</td>
                        <td className="px-4 py-3 text-center text-white">{l.qty}</td>
                        <td className="px-4 py-3 text-right">{l.tbc ? <span className="text-amber-400 text-xs">Price TBC</span> : <span className="text-white">{gbp(l.unit_cvd)}</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: "#0c1218" }} className="font-semibold">
                    <td className="px-4 py-3 text-white" colSpan={3}>Total{bom.totals.has_tbc ? " (excl. TBC items)" : ""}</td>
                    <td className="px-4 py-3 text-right" style={{ color: ACCENT }} data-testid="securest-total">{gbp(bom.totals.cvd)}</td>
                  </tr>
                </tfoot>
              </table></div>
            )}

            {bom.download_notes?.length > 0 && (
              <div data-testid="securest-download-notes" className="px-6 py-4 border-t space-y-2" style={{ borderColor: "#1c2530" }}>
                {bom.download_notes.map((n, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-amber-300"><AlertTriangle className="h-4 w-4 flex-shrink-0" />{n}</div>
                ))}
              </div>
            )}

            {bom.notes?.length > 0 && (
              <div className="px-6 py-4 border-t text-xs space-y-1" style={{ borderColor: "#1c2530", color: "#8b98a8" }}>
                {bom.notes.map((n, i) => <div key={i}>• {n}</div>)}
              </div>
            )}
          </div>
        )}
      </main>
      {showSaved && <SecurestSavedQuotes onClose={() => setShowSaved(false)} />}

      {wallDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: "rgba(0,0,0,0.65)" }} data-testid="securest-wallmount-dialog">
          <div className="rounded-xl border p-6 w-full max-w-sm" style={{ borderColor: "#28323e", backgroundColor: "#0f151c" }}>
            <div className="flex items-center gap-2 text-white font-semibold text-lg"><ScanFace className="h-5 w-5" style={{ color: ACCENT }} />Wall mounts</div>
            <p className="text-sm mt-2" style={{ color: "#94a3b8" }}>Do you require wall mounts? One V3L wall bracket is added for each face reader.</p>
            <div className="grid grid-cols-2 gap-2 mt-5">
              <button data-testid="securest-wallmount-yes" onClick={() => answerWallMounts(true)} className="rounded-lg py-2.5 font-semibold text-white" style={{ backgroundColor: ACCENT }}>Yes</button>
              <button data-testid="securest-wallmount-no" onClick={() => answerWallMounts(false)} className="rounded-lg py-2.5 font-semibold border" style={{ borderColor: "#28323e", color: "#cbd5e1" }}>No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
